import { formatIsoDate, getLocaleObject } from '@/lib/utils';
import {
  Button,
  CalendarInput,
  DataTableColumnType,
  DataTableFilterConfig,
  DataTableFilterItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from '@opaz/ui-components';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

const ANY_FILTER_VALUE = '__any__';

function renderColumnValue<T>(
  column: DataTableColumnType<T>,
  record: T,
  recordIndex: number,
  fallback: string,
): ReactNode {
  if ('render' in column && column.render) {
    return column.render(record, recordIndex) ?? fallback;
  }

  if (!('dataIndex' in column) || column.dataIndex == null) {
    return fallback;
  }

  const value = record[column.dataIndex];
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return fallback;
}

type ResponsiveDataViewProps<T> = {
  children: ReactNode;
  columns: DataTableColumnType<T>[];
  data: T[];
  isLoading?: boolean;
  emptyMessage: string;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  filterConfig?: DataTableFilterConfig | null;
  onFilterChange?: (config: DataTableFilterConfig) => void;
  getRowKey?: (record: T, index: number) => string | number;
};

export function ResponsiveDataView<T>({
  children,
  columns,
  data,
  isLoading = false,
  emptyMessage,
  currentPage,
  totalPages,
  onPageChange,
  filterConfig,
  onFilterChange,
  getRowKey,
}: ResponsiveDataViewProps<T>) {
  const { t, i18n } = useTranslation();
  const showPagination =
    currentPage !== undefined && totalPages !== undefined && totalPages > 1 && onPageChange !== undefined;
  const filterableColumns = columns.filter((column) => column.filter);
  const activeFilterCount = Object.keys(filterConfig ?? {}).length;

  const updateFilter = (key: string, item: DataTableFilterItem | null) => {
    const nextConfig = { ...(filterConfig ?? {}) };

    if (item) {
      nextConfig[key] = item;
    } else {
      delete nextConfig[key];
    }

    onFilterChange?.(nextConfig);
  };

  return (
    <>
      <div className="hidden md:block">{children}</div>

      <div className="md:hidden">
        {onFilterChange && filterableColumns.length > 0 && (
          <div className="mb-4 space-y-3">
            {filterableColumns.map((column) => {
              const filter = column.filter;
              if (!filter) return null;

              const activeFilter = filterConfig?.[filter.key];

              const isStartDateRange = filter.key === 'startDateRange';

              return (
                <div key={filter.key} className="block">
                  {filter.type !== 'date-range' && (
                    <p className="mb-1.5 text-sm font-medium text-foreground">{column.title}</p>
                  )}
                  {filter.type === 'select' && (
                    <Select
                      value={activeFilter?.type === 'select' ? activeFilter.value : ANY_FILTER_VALUE}
                      onValueChange={(value) => {
                        updateFilter(filter.key, value === ANY_FILTER_VALUE ? null : { type: 'select', value });
                      }}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder={t('common.table.filter.any')} />
                      </SelectTrigger>
                      <SelectContent
                        position="popper"
                        collisionPadding={{ top: 16, bottom: 88, left: 12, right: 12 }}
                        className="z-[1100] max-h-[min(16rem,calc(100svh-10rem))] overflow-y-auto overscroll-contain"
                      >
                        <SelectItem value={ANY_FILTER_VALUE}>{t('common.table.filter.any')}</SelectItem>
                        {filter.options?.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {filter.type === 'date' && (
                    <div className="w-full [&>div]:w-full">
                      <CalendarInput
                        locale={getLocaleObject(i18n.language)}
                        dateFormatter={(date) => formatIsoDate(date.toISOString(), i18n.language)}
                        value={activeFilter?.type === 'date' ? activeFilter.value : undefined}
                        onChange={(date) => {
                          updateFilter(filter.key, date ? { type: 'date', value: date } : null);
                        }}
                        placeholder={t('common.selectDate')}
                        clearLabel={t('common.table.filter.clear')}
                      />
                    </div>
                  )}
                  {filter.type === 'date-range' && (
                    <div className="space-y-3">
                      <div>
                        <p className="mb-1.5 text-sm font-medium text-foreground">
                          {t(
                            isStartDateRange ? 'common.table.filter.startDateFrom' : 'common.table.filter.endDateFrom',
                          )}
                        </p>
                        <div className="w-full [&>div]:w-full">
                          <CalendarInput
                            locale={getLocaleObject(i18n.language)}
                            dateFormatter={(date) => formatIsoDate(date.toISOString(), i18n.language)}
                            value={activeFilter?.type === 'date-range' ? activeFilter.value.from : undefined}
                            onChange={(from) => {
                              const to = activeFilter?.type === 'date-range' ? activeFilter.value.to : null;
                              if (!from && !to) {
                                updateFilter(filter.key, null);
                                return;
                              }

                              updateFilter(filter.key, {
                                type: 'date-range',
                                value: { from: from ?? to ?? new Date(), to },
                              });
                            }}
                            placeholder={t('common.selectDate')}
                            clearLabel={t('common.table.filter.clear')}
                          />
                        </div>
                      </div>
                      <div>
                        <p className="mb-1.5 text-sm font-medium text-foreground">
                          {t(isStartDateRange ? 'common.table.filter.startDateTo' : 'common.table.filter.endDateTo')}
                        </p>
                        <div className="w-full [&>div]:w-full">
                          <CalendarInput
                            locale={getLocaleObject(i18n.language)}
                            dateFormatter={(date) => formatIsoDate(date.toISOString(), i18n.language)}
                            value={
                              activeFilter?.type === 'date-range' ? (activeFilter.value.to ?? undefined) : undefined
                            }
                            onChange={(to) => {
                              const from = activeFilter?.type === 'date-range' ? activeFilter.value.from : undefined;
                              if (!from && !to) {
                                updateFilter(filter.key, null);
                                return;
                              }

                              updateFilter(filter.key, {
                                type: 'date-range',
                                value: { from: from ?? to ?? new Date(), to: to ?? null },
                              });
                            }}
                            placeholder={t('common.selectDate')}
                            clearLabel={t('common.table.filter.clear')}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {activeFilterCount > 0 && (
              <Button variant="secondary" className="w-full" onClick={() => onFilterChange({})}>
                {t('common.table.filter.clearAllFilters')}
              </Button>
            )}
          </div>
        )}

        {isLoading ? (
          <div className="space-y-3" role="status" aria-label={t('common.loading')}>
            {Array.from({ length: 3 }).map((_, cardIndex) => (
              <div key={cardIndex} className="space-y-3 rounded-xl border border-border bg-white p-4 shadow-sm">
                {columns.map((_, columnIndex) => (
                  <div key={columnIndex} className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-3">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : data.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border px-4 py-10 text-center text-muted-foreground">
            {emptyMessage}
          </div>
        ) : (
          <div className="space-y-3">
            {data.map((record, recordIndex) => (
              <article
                key={getRowKey?.(record, recordIndex) ?? recordIndex}
                className="rounded-xl border border-border bg-white p-4 shadow-sm"
              >
                <dl className="divide-y divide-border">
                  {columns.map((column, columnIndex) => (
                    <div
                      key={columnIndex}
                      className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] items-start gap-3 py-3 first:pt-0 last:pb-0"
                    >
                      <dt className="text-sm font-medium text-muted-foreground">
                        {column.title || t('common.table.actions')}
                      </dt>
                      <dd className="min-w-0 break-words text-sm text-foreground [&>*]:max-w-full [&_*]:max-w-full [&_.items-end]:items-start">
                        {renderColumnValue(column, record, recordIndex, t('common.unknown'))}
                      </dd>
                    </div>
                  ))}
                </dl>
              </article>
            ))}
          </div>
        )}

        {showPagination && (
          <nav className="mt-5 flex items-center justify-between gap-3" aria-label={t('common.table.pagination')}>
            <Button
              variant="secondary"
              size="icon"
              disabled={currentPage <= 1}
              onClick={() => onPageChange(currentPage - 1)}
              aria-label={t('common.table.previous')}
            >
              <ChevronLeft className="h-4 w-4 rtl:rotate-180" aria-hidden="true" />
            </Button>
            <span className="text-sm text-muted-foreground">
              {t('common.table.pageStatus', { current: currentPage, total: totalPages })}
            </span>
            <Button
              variant="secondary"
              size="icon"
              disabled={currentPage >= totalPages}
              onClick={() => onPageChange(currentPage + 1)}
              aria-label={t('common.table.next')}
            >
              <ChevronRight className="h-4 w-4 rtl:rotate-180" aria-hidden="true" />
            </Button>
          </nav>
        )}
      </div>
    </>
  );
}
